// DataGrid 커스텀 페이지네이션 — 페이지 번호 버튼 + 페이지 직접 이동.
// 기본 MuiTablePagination(이전/다음 화살표만)을 페이지 번호 버튼 방식으로 교체하고,
// 존재하지 않는 페이지 번호를 입력하면 이동 없이 알림만 띄운다.
// slots.pagination 으로 DataGridPanel에 연결한다(사용자 제공 레퍼런스 UX 반영).

import React from "react";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import {
  gridPageSelector,
  gridPageSizeSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { useNotification } from "@refinedev/core";

export interface CustomPaginationProps {
  // DataGridPanel의 pageSizeOptions를 그대로 전달받아 "페이지 당 행" 선택지로 쓴다.
  pageSizeOptions?: number[];
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
  pageSizeOptions = [20, 50, 100],
}) => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  // 주의: MUI의 기본 gridPageCountSelector는 "클라이언트에 이미 로드된 행 수" 기준으로
  // 계산돼, 이 프로젝트처럼 서버 페이지네이션(paginationMode: "server")을 쓰는 그리드에서는
  // 항상 1페이지로 잘못 계산된다. rowCount(서버 전체 건수) 기준으로 직접 계산해야 한다.
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(rowCount / pageSize)) : 0;
  const { open } = useNotification();

  const [jumpValue, setJumpValue] = React.useState("");

  // 숫자 + 범위 검증 후 이동. 잘못된 값이면 페이지 이동 없이 알림만 띄운다.
  const jumpToPage = () => {
    const trimmed = jumpValue.trim();
    if (trimmed === "") return;

    const target = Number(trimmed);
    const isValid = /^[0-9]+$/.test(trimmed) && target >= 1 && target <= pageCount;

    if (isValid) {
      apiRef.current.setPage(target - 1);
      setJumpValue("");
    } else {
      open?.({
        key: "grid-page-jump-invalid",
        type: "error",
        message: "존재하지 않는 페이지입니다.",
        description: pageCount > 0 ? `1~${pageCount} 사이의 페이지 번호를 입력해 주세요.` : undefined,
      });
    }
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    apiRef.current.setPageSize(Number(event.target.value));
  };

  // 행이 아예 없으면(빈 상태) 페이지네이션 자체를 숨긴다.
  if (rowCount <= 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        width: "100%",
        px: 2,
        py: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          페이지 당 행
        </Typography>
        <Select
          size="small"
          value={pageSize}
          onChange={handlePageSizeChange}
          sx={{ minWidth: 72 }}
          inputProps={{ "aria-label": "페이지 당 행 수" }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          전체 {rowCount.toLocaleString()}건
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Pagination
          color="primary"
          variant="outlined"
          shape="rounded"
          page={page + 1}
          count={pageCount}
          siblingCount={1}
          boundaryCount={1}
          onChange={(_event, value) => apiRef.current.setPage(value - 1)}
        />
        <TextField
          size="small"
          placeholder="이동"
          value={jumpValue}
          onChange={(event) => setJumpValue(event.target.value.replace(/[^0-9]/g, ""))}
          onKeyDown={(event) => {
            if (event.key === "Enter") jumpToPage();
          }}
          inputProps={{ inputMode: "numeric", "aria-label": "이동할 페이지 번호" }}
          sx={{ width: 76 }}
        />
        <IconButton size="small" aria-label="입력한 페이지로 이동" onClick={jumpToPage}>
          <SearchIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
